<template>
  <div class="space-y-4">
    <!-- En-tête avec icône  -->
    <div class="text-center mb-4">
      <div
        class="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-2"
      >
        <i class="fas fa-clipboard-check text-white text-lg"></i>
      </div>
      <h3 class="text-xl font-bold text-gray-900 mb-1">{{ title }}</h3>
      <p class="text-sm text-gray-600">{{ subtitle }}</p>
    </div>

    <!-- Résumé des informations -->
    <div
      class="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 space-y-4 shadow-lg border border-gray-100"
    >
      <!-- Informations générales  -->
      <div
        class="bg-white rounded-lg py-3 px-4 shadow-sm border border-gray-200"
      >
        <div class="flex items-center space-x-2 mb-2">
          <div
            class="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center"
          >
            <i class="fas fa-building text-white text-sm"></i>
          </div>
          <h4 class="text-base font-semibold text-gray-900">
            Informations générales
          </h4>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
          <div
            class="flex items-center space-x-2 py-1 px-2 bg-gray-50 rounded-md"
          >
            <i
              class="fas fa-building text-blue-600 w-4 text-xs hidden sm:block"
            ></i>
            <div class="min-w-0">
              <p class="text-xs text-gray-600 mb-0">Société</p>
              <p class="font-medium text-gray-900 text-sm truncate">
                {{ data.companyName || "Non renseigné" }}
              </p>
            </div>
          </div>
          <div
            class="flex items-center space-x-2 py-1 px-2 bg-gray-50 rounded-md"
          >
            <i
              class="fas fa-tags text-purple-600 w-4 text-xs hidden sm:block"
            ></i>
            <div class="min-w-0">
              <p class="text-xs text-gray-600 mb-0">Marques</p>
              <p class="font-medium text-gray-900 text-sm truncate">
                {{ formatBrands() }}
              </p>
            </div>
          </div>
          <div
            class="flex items-center space-x-2 py-1 px-2 bg-gray-50 rounded-md"
          >
            <i
              class="fas fa-user text-green-600 w-4 text-xs hidden sm:block"
            ></i>
            <div class="min-w-0">
              <p class="text-xs text-gray-600 mb-0">Contact</p>
              <p class="font-medium text-gray-900 text-sm truncate">
                {{ formatContact() }}
              </p>
            </div>
          </div>
          <div
            class="flex items-center space-x-2 py-1 px-2 bg-gray-50 rounded-md"
          >
            <i
              class="fas fa-envelope text-orange-600 w-4 text-xs hidden sm:block"
            ></i>
            <div class="min-w-0">
              <p class="text-xs text-gray-600 mb-0">Email</p>
              <p class="font-medium text-gray-900 text-sm truncate">
                {{ data.email || "Non renseigné" }}
              </p>
            </div>
          </div>
          <div
            class="flex items-center space-x-2 py-1 px-2 bg-gray-50 rounded-md"
          >
            <i
              class="fas fa-phone text-indigo-600 w-4 text-xs hidden sm:block"
            ></i>
            <div class="min-w-0">
              <p class="text-xs text-gray-600 mb-0">Téléphone</p>
              <p class="font-medium text-gray-900 text-sm truncate">
                {{ data.phone || "Non renseigné" }}
              </p>
            </div>
          </div>
          <div
            class="flex items-center space-x-2 py-1 px-2 bg-gray-50 rounded-md"
          >
            <i
              class="fas fa-mobile-alt text-pink-600 w-4 text-xs hidden sm:block"
            ></i>
            <div class="min-w-0">
              <p class="text-xs text-gray-600 mb-0">Mobile</p>
              <p class="font-medium text-gray-900 text-sm truncate">
                {{ data.mobile || "Non renseigné" }}
              </p>
            </div>
          </div>
          <div
            class="flex items-center space-x-2 py-1 px-2 bg-gray-50 rounded-md"
          >
            <i class="fas fa-fax text-teal-600 w-4 text-xs hidden sm:block"></i>
            <div class="min-w-0">
              <p class="text-xs text-gray-600 mb-0">Fax</p>
              <p class="font-medium text-gray-900 text-sm truncate">
                {{ data.fax || "Non renseigné" }}
              </p>
            </div>
          </div>
          <div
            class="flex items-center space-x-2 py-1 px-2 bg-gray-50 rounded-md"
          >
            <i
              class="fas fa-calendar text-amber-600 w-4 text-xs hidden sm:block"
            ></i>
            <div class="min-w-0">
              <p class="text-xs text-gray-600 mb-0">Création</p>
              <p class="font-medium text-gray-900 text-sm truncate">
                {{ data.creationDate || "Non renseigné" }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Logo et Localisation -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Logo de l'entreprise  -->
        <div
          class="bg-white rounded-lg py-3 px-4 shadow-sm border border-gray-200"
        >
          <div class="flex items-center space-x-2 mb-2">
            <div
              class="w-8 h-8 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center"
            >
              <i class="fas fa-image text-white text-sm"></i>
            </div>
            <h4 class="text-base font-semibold text-gray-900">Logo</h4>
          </div>
          <div v-if="logoFile" class="flex items-center space-x-3">
            <img
              :src="getImagePreview(logoFile)"
              :alt="logoFile.name"
              class="w-48 h-48 object-cover rounded-lg border-2 border-gray-200 shadow-sm"
            />
            <div class="space-y-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 truncate">
                {{ logoFile.name }}
              </p>
              <p class="text-xs text-gray-600">
                {{ formatFileSize(logoFile.size) }}
              </p>
            </div>
          </div>
          <div v-else class="text-center py-4">
            <i class="fas fa-image text-gray-300 text-2xl mb-1"></i>
            <p class="text-xs text-gray-500">Aucun logo</p>
          </div>
        </div>

        <!-- Adresse et localisation  -->
        <div
          class="bg-white rounded-lg py-3 px-4 shadow-sm border border-gray-200"
        >
          <div class="flex items-center space-x-2 mb-2">
            <div
              class="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center"
            >
              <i class="fas fa-map-marker-alt text-white text-sm"></i>
            </div>
            <h4 class="text-base font-semibold text-gray-900">Localisation</h4>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div
              class="flex items-center space-x-2 py-1 px-2 bg-gray-50 rounded-md"
            >
              <i
                class="fas fa-road text-green-600 w-4 text-xs hidden sm:block"
              ></i>
              <div class="min-w-0">
                <p class="text-xs text-gray-600 mb-0">Rue</p>
                <p class="font-medium text-gray-900 text-sm truncate">
                  {{ data.streetAddress || "Non renseigné" }}
                </p>
              </div>
            </div>
            <div
              class="flex items-center space-x-2 py-1 px-2 bg-gray-50 rounded-md"
            >
              <i
                class="fas fa-mail-bulk text-green-600 w-4 text-xs hidden sm:block"
              ></i>
              <div class="min-w-0">
                <p class="text-xs text-gray-600 mb-0">Code postal</p>
                <p class="font-medium text-gray-900 text-sm truncate">
                  {{ data.postalCode || "Non renseigné" }}
                </p>
              </div>
            </div>
            <div
              class="flex items-center space-x-2 py-1 px-2 bg-gray-50 rounded-md"
            >
              <i
                class="fas fa-city text-green-600 w-4 text-xs hidden sm:block"
              ></i>
              <div class="min-w-0">
                <p class="text-xs text-gray-600 mb-0">Ville</p>
                <p class="font-medium text-gray-900 text-sm truncate">
                  {{ data.city || "Non renseigné" }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Informations bancaires et commerciales -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Informations bancaires  -->
        <div
          class="bg-white rounded-lg py-3 px-4 shadow-sm border border-gray-200"
        >
          <div class="flex items-center space-x-2 mb-2">
            <div
              class="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center"
            >
              <i class="fas fa-university text-white text-sm"></i>
            </div>
            <h4 class="text-base font-semibold text-gray-900">
              Informations bancaires
            </h4>
          </div>
          <div class="space-y-2">
            <div
              class="flex items-center space-x-2 py-1 px-2 bg-gray-50 rounded-md"
            >
              <i
                class="fas fa-user-circle text-blue-600 w-4 text-xs hidden sm:block"
              ></i>
              <div class="min-w-0">
                <p class="text-xs text-gray-600 mb-0">Titulaire du compte</p>
                <p class="font-medium text-xs text-gray-900 truncate">
                  {{ bankingInfo?.accountTitle || "Non renseigné" }}
                </p>
              </div>
            </div>
            <div
              class="flex items-center space-x-2 py-1 px-2 bg-gray-50 rounded-md"
            >
              <i
                class="fas fa-credit-card text-emerald-600 w-4 text-xs hidden sm:block"
              ></i>
              <div class="min-w-0">
                <p class="text-xs text-gray-600 mb-0">IBAN</p>
                <p class="font-mono text-xs text-gray-900 truncate">
                  {{ bankingInfo?.iban || "Non renseigné" }}
                </p>
              </div>
            </div>
            <div
              class="flex items-center space-x-2 py-1 px-2 bg-gray-50 rounded-md"
            >
              <i
                class="fas fa-receipt text-cyan-600 w-4 text-xs hidden sm:block"
              ></i>
              <div class="min-w-0">
                <p class="text-xs text-gray-600 mb-0">TVA</p>
                <p class="font-mono text-xs text-gray-900 truncate">
                  {{ bankingInfo?.tva || "Non renseigné" }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Informations commerciales  -->
        <div
          class="bg-white rounded-lg py-3 px-4 shadow-sm border border-gray-200"
        >
          <div class="flex items-center space-x-2 mb-2">
            <div
              class="w-8 h-8 bg-gradient-to-br from-purple-500 to-violet-600 rounded-lg flex items-center justify-center"
            >
              <i class="fas fa-chart-line text-white text-sm"></i>
            </div>
            <h4 class="text-base font-semibold text-gray-900">
              Informations commerciales
            </h4>
          </div>
          <div class="space-y-2">
            <div
              class="flex items-center space-x-2 py-1 px-2 bg-gray-50 rounded-md"
            >
              <i
                class="fas fa-th-large text-purple-600 w-4 text-xs hidden sm:block"
              ></i>
              <div class="min-w-0">
                <p class="text-xs text-gray-600 mb-0">Catégories</p>
                <p class="font-medium text-gray-900 text-sm truncate">
                  {{ formatCategories() }}
                </p>
              </div>
            </div>
            <div
              class="flex items-center space-x-2 py-1 px-2 bg-gray-50 rounded-md"
            >
              <i
                class="fas fa-euro-sign text-violet-600 w-4 text-xs hidden sm:block"
              ></i>
              <div class="min-w-0">
                <p class="text-xs text-gray-600 mb-0">Gamme de prix</p>
                <p class="font-medium text-gray-900 text-sm truncate">
                  {{ formatPriceRange() }}
                </p>
              </div>
            </div>
            <div
              class="flex items-center space-x-2 py-1 px-2 bg-gray-50 rounded-md"
            >
              <i
                class="fas fa-shopping-cart text-indigo-600 w-4 text-xs hidden sm:block"
              ></i>
              <div class="min-w-0">
                <p class="text-xs text-gray-600 mb-0">Min. commande</p>
                <p class="font-medium text-gray-900 text-sm truncate">
                  {{ data.minOrder || "Non renseigné" }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Documents - compact -->
      <div
        class="bg-white rounded-lg py-3 px-4 shadow-sm border border-gray-200"
      >
        <div class="flex items-center space-x-2 mb-2">
          <div
            class="w-8 h-8 bg-gradient-to-br from-red-500 to-pink-600 rounded-lg flex items-center justify-center"
          >
            <i class="fas fa-file-alt text-white text-sm"></i>
          </div>
          <h4 class="text-base font-semibold text-gray-900">Documents</h4>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <!-- Kbis -->
          <div
            class="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200"
          >
            <div class="flex items-center space-x-3">
              <div
                class="document-icon hidden sm:flex w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg items-center justify-center"
              >
                <i class="fas fa-file-pdf text-white text-lg"></i>
              </div>
              <div class="min-w-0">
                <p class="font-semibold text-blue-900 text-sm">Kbis</p>
                <p class="text-xs text-blue-700 truncate">
                  {{ data.kbis ? formatFileName(data.kbis) : "Aucun document" }}
                </p>
              </div>
            </div>
            <div
              v-if="data.kbis"
              class="px-2 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-medium rounded-full"
            >
              <i class="fas fa-check text-xs mr-1"></i>OK
            </div>
          </div>

          <!-- Pièce d'identité gérant -->
          <div
            class="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200"
          >
            <div class="flex items-center space-x-3">
              <div
                class="document-icon hidden sm:flex w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg items-center justify-center"
              >
                <i class="fas fa-id-card text-white text-lg"></i>
              </div>
              <div class="min-w-0">
                <p class="font-semibold text-green-900 text-sm">ID Gérant</p>
                <p class="font-medium text-gray-900 text-sm truncate">
                  {{
                    data.idGerant
                      ? formatFileName(data.idGerant)
                      : "Aucun document"
                  }}
                </p>
              </div>
            </div>
            <div
              v-if="data.idGerant"
              class="px-2 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-medium rounded-full"
            >
              <i class="fas fa-check text-xs mr-1"></i>OK
            </div>
          </div>
        </div>
      </div>

      <!-- Description -->
      <div
        class="bg-white rounded-lg py-3 px-4 shadow-sm border border-gray-200"
      >
        <div class="flex items-center space-x-2 mb-2">
          <div
            class="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center"
          >
            <i class="fas fa-align-left text-white text-sm"></i>
          </div>
          <h4 class="text-base font-semibold text-gray-900">Description</h4>
        </div>
        <div class="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <p class="text-gray-800 text-sm leading-relaxed">
            {{ data.description || "Aucune description fournie" }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { FormSummaryProps } from "../../types/sellerRegister";

const props = defineProps<FormSummaryProps>();

const formatBrands = () => {
  const brands = props.data.brand;
  if (brands && brands.length > 0) {
    return brands.join(", ");
  }
  return "Aucune marque sélectionnée";
};

const formatContact = () => {
  const { contactName, firstName } = props.data;
  if (contactName && firstName) {
    return `${contactName} ${firstName}`;
  } else if (contactName) {
    return contactName;
  } else if (firstName) {
    return firstName;
  }
  return "Non renseigné";
};

const formatCategories = () => {
  const categories = props.data.productCategories;
  if (categories && categories.length > 0) {
    return `${categories.length} catégorie(s) sélectionnée(s)`;
  }
  return "Aucune catégorie sélectionnée";
};

const formatPriceRange = () => {
  if (props.priceRange) {
    const { min, max } = props.priceRange;
    if (min !== undefined && max !== undefined) {
      return `${min}€ - ${max}€`;
    }
  }
  return "Non renseigné";
};
const formatFileName = (file: File) => {
  if (!file) return "Fichier inconnu";

  // Afficher le nom du fichier avec sa taille
  const sizeInKB = Math.round(file.size / 1024);
  const sizeText =
    sizeInKB > 1024 ? `${Math.round(sizeInKB / 1024)} MB` : `${sizeInKB} KB`;

  return `${file.name} (${sizeText})`;
};

// Vérifier si le fichier est une image
const isImageFile = (file: File): boolean => {
  return file.type.startsWith("image/");
};

// Créer un aperçu de l'image
const getImagePreview = (file: File): string => {
  if (!isImageFile(file)) return "";

  // Créer une URL temporaire pour l'aperçu
  return URL.createObjectURL(file);
};

// Formater la taille du fichier
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};
</script>

<style scoped>
/* Forcer le masquage des icônes des documents sur mobile */
@media (max-width: 640px) {
  .document-icon {
    display: none !important;
  }
}
</style>
